/**
 * LearnPage — My Learning + Course Catalogue (redesigned).
 *
 * Route: /learn   → My Learning tab
 * Route: /catalog → Catalogue tab (redesigned list/grid view)
 *
 * Catalogue tab features (new):
 *   - Filter sidebar: Categories (with counts), Status checkboxes,
 *     Duration dropdown, Level dropdown, Clear Filters
 *   - Status tabs: All Courses | Mandatory | In Progress | Completed
 *   - View toggle: List view (new) / Grid view (existing cards)
 *   - Course list rows with thumbnail, badge, progress, action buttons
 *   - Pagination (10 per page)
 *
 * All existing My Learning logic preserved.
 */

import { useMemo, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEnrollments } from '../hooks/useEnrollments';
import type { Enrollment } from '../hooks/useEnrollments';
import { useAuth } from '../hooks/useAuth';
import { useLearningPaths } from '../hooks/useLearningPaths';
import type { LearningPath } from '../hooks/useLearningPaths';
import { AgentLevelBadge } from '../components/AgentLevelBadge';
import { TrackProgressSection } from '../components/TrackProgressSection';
import type { TrackProgress } from '../components/TrackProgressSection';
import { EnrolledCourseCard, CatalogCourseCard } from '../components/CourseCard';
import { StarRating } from '../components/StarRating';
import { RecommendationPanel } from '../components/RecommendationPanel/RecommendationPanel';
import { DrillCard } from '../components/DrillCard';
import { useTenantConfig } from '../contexts/TenantConfigContext';
import type { CompetencyTrack, AgentLevel } from '../types';
import styles from './LearnPage.module.css';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Tab = 'my_learning' | 'catalog';
type ViewMode = 'list' | 'grid';
type StatusFilter = 'all' | 'mandatory' | 'in_progress' | 'completed';

// Unified row for list display: either enrolled course or catalog course
interface CourseRow {
  id: string;           // enrollment id or catalog id
  courseId: string;
  title: string;
  track: CompetencyTrack | null;
  moduleType: string;
  minLevel: string | null;
  language: string;
  isMandatory: boolean;
  isEnrolled: boolean;
  enrollmentId: string | null;
  status: string | null;  // 'not_started' | 'in_progress' | 'completed' | null
  pct: number;
  dueDate: string | null;
  allowSelfEnroll: boolean;
  durationMinutes: number | null;
  averageRating: number | null;
  ratingCount: number;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const LEVEL_INT: Record<string, number> = { L1: 1, L2: 2, L3: 3, L4: 4, L5: 5 };
const PAGE_SIZE = 10;

const CATEGORIES: { value: string; label: string }[] = [
  { value: '',           label: 'All Courses'         },
  { value: 'product',    label: 'Product Knowledge'   },
  { value: 'sales',      label: 'Sales Skills'        },
  { value: 'compliance', label: 'Compliance'          },
  { value: 'roleplay',   label: 'Roleplay Practice'   },
  { value: 'leadership', label: 'Leadership'          },
];

const DURATION_OPTIONS = [
  { value: '', label: 'Any Duration' },
  { value: 'short',  label: 'Under 20 mins' },
  { value: 'medium', label: '20–45 mins'    },
  { value: 'long',   label: '45+ mins'      },
];

const LEVEL_OPTIONS = [
  { value: '',   label: 'Any Level'     },
  { value: 'L1', label: 'Beginner'     },
  { value: 'L2', label: 'Intermediate' },
  { value: 'L3', label: 'Advanced'     },
];

// Track label map for display
const TRACK_LABEL: Record<string, string> = {
  product:    'Product Knowledge',
  sales:      'Sales Skills',
  compliance: 'Compliance',
  roleplay:   'Roleplay Practice',
  leadership: 'Leadership',
};

// Thumbnail gradients per module type (mirrors DashboardPage / CourseCard).
const MODULE_GRADIENTS: Record<string, string> = {
  roleplay: 'linear-gradient(135deg, #0055a5 0%, #003d7a 100%)',
  quiz:     'linear-gradient(135deg, #6d28d9 0%, #4c1d95 100%)',
  reading:  'linear-gradient(135deg, #0891b2 0%, #0e7490 100%)',
  video:    'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
};

// ---------------------------------------------------------------------------
// Icon helpers
// ---------------------------------------------------------------------------

function CourseThumbIcon({ track, moduleType }: { track: string | null; moduleType: string }) {
  const color = track === 'compliance' ? '#0055a5'
    : track === 'leadership' ? '#1a7a4a'
    : track === 'sales'      ? '#c95f00'
    : track === 'soft_skills'? '#6b3fa0'
    : '#0891b2';

  const bg = track === 'compliance' ? '#e8f0fb'
    : track === 'leadership' ? '#e6f4ed'
    : track === 'sales'      ? '#fff7ed'
    : track === 'soft_skills'? '#f0e8fa'
    : '#e0f4f8';

  const icon = moduleType === 'roleplay'
    ? <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"><path d="M9 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
    : moduleType === 'quiz'
    ? <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17" strokeWidth="2.5"/></svg>
    : moduleType === 'video'
    ? <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></svg>
    : <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>;

  return (
    <div style={{ width: 52, height: 52, borderRadius: 10, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      {icon}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Course list row
// ---------------------------------------------------------------------------

function CourseListRow({
  row,
  onContinue,
  onStart,
  onOpenFeedback,
  enrollingId,
}: {
  row: CourseRow;
  onContinue: (enrollmentId: string) => void;
  onStart: (courseId: string) => void;
  onOpenFeedback: (courseId: string) => void;
  enrollingId: string | null;
}): JSX.Element {
  const levelLabel = row.minLevel ? (LEVEL_INT[row.minLevel] <= 1 ? 'Beginner' : LEVEL_INT[row.minLevel] <= 2 ? 'Intermediate' : 'Advanced') : 'Beginner';
  const categoryLabel = row.track ? (TRACK_LABEL[row.track] ?? row.track) : '';

  return (
    <div className={styles.courseRow}>
      <CourseThumbIcon track={row.track} moduleType={row.moduleType} />

      <div className={styles.courseRowBody}>
        <div className={styles.courseRowTitleRow}>
          <span className={styles.courseRowTitle}>{row.title}</span>
          {row.isMandatory && <span className={`${styles.courseBadge} ${styles.badgeMandatory}`}>Mandatory</span>}
          {row.isEnrolled && !row.isMandatory && <span className={`${styles.courseBadge} ${styles.badgeAssigned}`}>Assigned</span>}
        </div>
        <div className={styles.courseRowMeta}>
          {row.durationMinutes != null && <><span>{row.durationMinutes} mins</span><span className={styles.courseRowMetaDot}>·</span></>}
          <span>{levelLabel}</span>
          {categoryLabel && <><span className={styles.courseRowMetaDot}>·</span><span>{categoryLabel}</span></>}
        </div>
        {(row.averageRating ?? 0) > 0 && (
          <button
            type="button"
            className={styles.courseRowRating}
            onClick={(event) => {
              event.stopPropagation();
              onOpenFeedback(row.courseId);
            }}
          >
            <StarRating value={row.averageRating ?? 0} size="sm" />
            <span className={styles.courseRowRatingText}>
              {(row.averageRating ?? 0).toFixed(2)}/5 ({row.ratingCount})
            </span>
          </button>
        )}
        {row.status === 'in_progress' && (
          <div className={styles.courseRowProgress}>
            <div className={styles.courseRowProgressTrack}>
              <div className={styles.courseRowProgressFill} style={{ width: `${row.pct}%` }} />
            </div>
            <span className={styles.courseRowProgressPct}>{row.pct}%</span>
          </div>
        )}
        {row.status === 'not_started' && (
          <div className={styles.courseRowMeta} style={{ marginTop: 3 }}>Not Started</div>
        )}
      </div>

      <div className={styles.courseRowActions}>
        {row.status === 'completed' ? (
          <span className={styles.actionCompleted}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
            Completed
          </span>
        ) : row.status === 'in_progress' ? (
          <button
            className={styles.actionBtnContinue}
            onClick={(e) => { e.stopPropagation(); row.enrollmentId && onContinue(row.enrollmentId); }}
            type="button"
          >
            Continue
          </button>
        ) : row.isEnrolled && row.enrollmentId ? (
          <button
            className={styles.actionBtnStart}
            onClick={(e) => { e.stopPropagation(); row.enrollmentId && onContinue(row.enrollmentId); }}
            type="button"
          >
            Start Course
          </button>
        ) : row.allowSelfEnroll ? (
          <button
            className={styles.actionBtnStart}
            onClick={(e) => { e.stopPropagation(); onStart(row.courseId); }}
            disabled={enrollingId === row.courseId}
            type="button"
          >
            {enrollingId === row.courseId ? 'Enrolling…' : 'Start Course'}
          </button>
        ) : (
          <span className={styles.actionNotStarted}>Not Started</span>
        )}
        <button className={styles.menuBtn} type="button" aria-label="More options" onClick={(e) => e.stopPropagation()}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="5" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="12" cy="19" r="1.5"/></svg>
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stat card (My Learning summary row — mirrors DashboardPage stat cards)
// ---------------------------------------------------------------------------

type StatAccent = 'blue' | 'green' | 'orange' | 'purple';

const STAT_THEME: Record<StatAccent, { bg: string; fg: string }> = {
  blue:   { bg: '#e8f0fb', fg: '#0055a5' },
  green:  { bg: '#e6f4ed', fg: '#1a7a4a' },
  orange: { bg: '#fff7ed', fg: '#c95f00' },
  purple: { bg: '#f0e8fa', fg: '#6b3fa0' },
};

function StatIcon({ accent }: { accent: StatAccent }): JSX.Element {
  const stroke = STAT_THEME[accent].fg;
  const common = { width: 20, height: 20, viewBox: '0 0 24 24', fill: 'none', stroke, strokeWidth: 2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  if (accent === 'blue') return (<svg {...common}><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>);
  if (accent === 'green') return (<svg {...common}><polyline points="20 6 9 17 4 12"/></svg>);
  if (accent === 'orange') return (<svg {...common}><path d="M9 2h6l1 3h4v15a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5h4z"/><line x1="9" y1="2" x2="15" y2="2"/></svg>);
  return (<svg {...common}><circle cx="12" cy="8" r="6"/><path d="M15.5 12.9L17 22l-5-3-5 3 1.5-9.1"/></svg>);
}

function StatCard({ label, value, accent }: { label: string; value: number; accent: StatAccent }): JSX.Element {
  const theme = STAT_THEME[accent];
  return (
    <div className={styles.statCard}>
      <span className={styles.statIcon} style={{ background: theme.bg }}>
        <StatIcon accent={accent} />
      </span>
      <span className={styles.statBody}>
        <span className={styles.statValue} style={{ color: theme.fg }}>{value}</span>
        <span className={styles.statLabel}>{label}</span>
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// My Learning helpers (unchanged from original)
// ---------------------------------------------------------------------------

function computeTrackProgress(
  enrollments: Enrollment[],
): TrackProgress[] {
  const tracks: CompetencyTrack[] = ['product', 'sales', 'compliance', 'leadership'];
  return tracks.map((track) => {
    const tc = enrollments.filter((e) => e.track === track);
    return { track, completed: tc.filter((e) => e.status === 'completed').length, total: tc.length };
  });
}

function LearningPathsSection({ paths }: { paths: LearningPath[] }): JSX.Element | null {
  const { t } = useTranslation('training');
  if (paths.length === 0) return null;
  return (
    <div className={styles.sectionBlock}>
      <h2 className={styles.sectionTitle}>{t('learn.learningPaths.title')}</h2>
      <div className={styles.pathList}>
        {paths.map((path) => {
          const pct = Math.round((path.current_course_sequence - 1) / path.total_courses * 100);
          return (
            <div key={path.id} className={styles.pathCard} data-status={path.status}>
              <div className={styles.pathHeader}>
                <span className={styles.pathTitle}>{path.title}</span>
                <span className={styles.pathStatus}>{t(`learn.learningPaths.status.${path.status}`)}</span>
              </div>
              <div className={styles.pathProgress}>
                <div className={styles.pathProgressBar} role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} style={{ width: `${pct}%` }} />
              </div>
              <p className={styles.pathStep}>{t('learn.learningPaths.step', { current: path.current_course_sequence, total: path.total_courses })}</p>
              {path.current_course && (
                <p className={styles.pathCurrentCourse}>{t('learn.learningPaths.nextCourse')}: {path.current_course.title}</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// LearnPage
// ---------------------------------------------------------------------------

export function LearnPage(): JSX.Element {
  const { t } = useTranslation('training');
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { enrollments, catalog, isLoading, error, enroll } = useEnrollments();
  const { paths: learningPaths } = useLearningPaths();
  const { featureFlags } = useTenantConfig();

  const initialTab: Tab = location.pathname === '/catalog' ? 'catalog' : 'my_learning';
  const [activeTab, setActiveTab] = useState<Tab>(initialTab);
  const [enrollingId, setEnrollingId] = useState<string | null>(null);

  // Catalogue state
  const [viewMode, setViewMode]             = useState<ViewMode>('list');
  const [statusFilter, setStatusFilter]     = useState<StatusFilter>('all');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterLevel, setFilterLevel]       = useState('');
  const [filterDuration, setFilterDuration] = useState('');
  const [filterMandatory, setFilterMandatory] = useState(false);
  const [filterInProgress, setFilterInProgress] = useState(false);
  const [filterCompleted, setFilterCompleted]   = useState(false);
  const [filterAssigned, setFilterAssigned]     = useState(false);
  const [searchQuery, setSearchQuery]       = useState('');
  const [page, setPage]                     = useState(1);

  useEffect(() => {
    setActiveTab(location.pathname === '/catalog' ? 'catalog' : 'my_learning');
  }, [location.pathname]);

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [statusFilter, filterCategory, filterLevel, filterMandatory, filterInProgress, filterCompleted, searchQuery]);

  const agentLevel = (user?.agent_level ?? 'L1') as AgentLevel;
  const agentLevelInt = LEVEL_INT[agentLevel] ?? 1;

  // My Learning derived data (redesigned — grouped by status, with summary stats)
  const trackProgress = useMemo(() => computeTrackProgress(enrollments), [enrollments]);

  const stats = useMemo(() => {
    const inProgress = enrollments.filter((e) => e.status === 'in_progress').length;
    const completed  = enrollments.filter((e) => e.status === 'completed' || e.status === 'failed').length;
    const mandatory  = enrollments.filter(
      (e) => e.is_mandatory && e.status !== 'completed' && e.status !== 'failed',
    ).length;
    const certificates = enrollments.filter(
      (e) => (e.status === 'completed' || e.status === 'failed') && e.is_mandatory,
    ).length;
    return { inProgress, completed, mandatory, certificates };
  }, [enrollments]);

  // Resume target: first non-completed enrollment (enrollments are pre-sorted
  // overdue → in_progress → not_started by useEnrollments, so [0]-ish is right).
  const continueEnrollment = useMemo(
    () => enrollments.find((e) => e.status !== 'completed' && e.status !== 'failed') ?? null,
    [enrollments],
  );

  const inProgressList = useMemo(
    () => enrollments.filter((e) => e.status === 'in_progress' && e.id !== continueEnrollment?.id),
    [enrollments, continueEnrollment],
  );
  const completedList = useMemo(
    () => enrollments.filter((e) => e.status === 'completed' || e.status === 'failed'),
    [enrollments],
  );

  const firstName = (user?.full_name ?? '').split(' ')[0];

  // Build unified course rows for catalogue
  const enrollmentMap = useMemo(() => {
    const m = new Map<string, Enrollment>();
    enrollments.forEach((e) => m.set(e.course_id, e));
    return m;
  }, [enrollments]);

  const allRows = useMemo((): CourseRow[] => {
    // Start with enrollments
    const rows: CourseRow[] = enrollments.map((e) => {
      const pct = e.modules_total > 0 ? Math.round((e.modules_completed / e.modules_total) * 100) : 0;
      return {
        id: e.id,
        courseId: e.course_id,
        title: e.course_title,
        track: e.track,
        moduleType: e.module_type,
        minLevel: e.min_level,
        language: e.language,
        isMandatory: e.is_mandatory,
        isEnrolled: true,
        enrollmentId: e.id,
        status: e.status,
        pct,
        dueDate: e.due_date,
        allowSelfEnroll: e.allow_self_enroll,
        durationMinutes: e.duration_minutes ?? null,
        averageRating: e.average_rating ?? null,
        ratingCount: e.rating_count ?? 0,
      };
    });

    // Add catalog courses not yet enrolled
    catalog.forEach((c) => {
      if (!enrollmentMap.has(c.id)) {
        if (c.min_level && LEVEL_INT[c.min_level] > agentLevelInt) return;
        if (c.max_level && LEVEL_INT[c.max_level] < agentLevelInt) return;
        rows.push({
          id: c.id,
          courseId: c.id,
          title: c.title,
          track: c.track,
          moduleType: c.module_type,
          minLevel: c.min_level,
          language: c.language,
          isMandatory: false,
          isEnrolled: false,
          enrollmentId: null,
          status: null,
          pct: 0,
          dueDate: null,
          allowSelfEnroll: c.allow_self_enroll,
          durationMinutes: c.duration_minutes ?? null,
          averageRating: c.average_rating ?? null,
          ratingCount: c.rating_count ?? 0,
        });
      }
    });

    return rows;
  }, [enrollments, catalog, enrollmentMap, agentLevelInt]);

  // Category counts
  const categoryCounts = useMemo(() => {
    const m: Record<string, number> = {};
    allRows.forEach((r) => {
      const t = r.track ?? 'other';
      m[t] = (m[t] ?? 0) + 1;
    });
    return m;
  }, [allRows]);

  // Status tab counts
  const statusCounts = useMemo(() => ({
    all:         allRows.length,
    mandatory:   allRows.filter((r) => r.isMandatory).length,
    in_progress: allRows.filter((r) => r.status === 'in_progress').length,
    completed:   allRows.filter((r) => r.status === 'completed').length,
  }), [allRows]);

  // Apply all filters
  const filteredRows = useMemo(() => {
    return allRows.filter((r) => {
      // Status tab
      if (statusFilter === 'mandatory'   && !r.isMandatory) return false;
      if (statusFilter === 'in_progress' && r.status !== 'in_progress') return false;
      if (statusFilter === 'completed'   && r.status !== 'completed') return false;
      // Category
      if (filterCategory && r.track !== filterCategory) return false;
      // Duration
      if (filterDuration) {
        const mins = r.durationMinutes ?? 45;
        if (filterDuration === 'short'  && mins >= 20) return false;
        if (filterDuration === 'medium' && (mins < 20 || mins > 45)) return false;
        if (filterDuration === 'long'   && mins <= 45) return false;
      }
      // Level
      if (filterLevel && r.minLevel && r.minLevel !== filterLevel) return false;
      // Checkbox filters
      if (filterMandatory && !r.isMandatory) return false;
      if (filterInProgress && r.status !== 'in_progress') return false;
      if (filterCompleted && r.status !== 'completed') return false;
      if (filterAssigned && !r.isEnrolled) return false;
      // Search
      if (searchQuery && !r.title.toLowerCase().includes(searchQuery.toLowerCase())) return false;
      return true;
    });
  }, [allRows, statusFilter, filterCategory, filterLevel, filterMandatory, filterInProgress, filterCompleted, filterAssigned, searchQuery]);

  // Pagination
  const totalPages  = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const pagedRows   = filteredRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Catalog courses for grid view (unenrolled)
  const catalogForGrid = useMemo(
    () => {
      const assignedCatalogRows = enrollments
        .filter((e) => e.assigned_by || e.is_mandatory)
        .map((e) => ({
          id: e.course_id,
          title: e.course_title,
          description: e.course_description,
          module_type: e.module_type,
          language: e.language,
          tags: e.tags,
          allow_self_enroll: e.allow_self_enroll,
          assigned_by: e.assigned_by ?? null,
          enrollment_id: e.id,
          status: e.status,
          track: e.track,
          min_level: e.min_level,
          max_level: null,
          banner_image: e.banner_image ?? null,
          duration_minutes: e.duration_minutes ?? null,
          average_rating: e.average_rating ?? null,
          rating_count: e.rating_count ?? 0,
        }));

      const merged = [...catalog, ...assignedCatalogRows].filter((c, index, arr) =>
        arr.findIndex((item) => item.id === c.id) === index,
      );

      return merged.filter((c) => {
        if (c.min_level && LEVEL_INT[c.min_level] > agentLevelInt) return false;
        if (c.max_level && LEVEL_INT[c.max_level] < agentLevelInt) return false;
        if (filterCategory && c.track !== filterCategory) return false;
        if (filterLevel && c.min_level && c.min_level !== filterLevel) return false;
        if (searchQuery && !c.title.toLowerCase().includes(searchQuery.toLowerCase())) return false;
        return true;
      });
    },
    [catalog, enrollments, agentLevelInt, filterCategory, filterLevel, searchQuery],
  );

  function clearFilters() {
    setFilterCategory('');
    setFilterLevel('');
    setFilterDuration('');
    setFilterMandatory(false);
    setFilterInProgress(false);
    setFilterCompleted(false);
    setFilterAssigned(false);
    setSearchQuery('');
    setStatusFilter('all');
  }

  async function handleEnroll(courseId: string): Promise<void> {
    setEnrollingId(courseId);
    try {
      await enroll(courseId);
      setActiveTab('my_learning');
      navigate('/learn');
    } finally {
      setEnrollingId(null);
    }
  }

  function handleContinue(enrollmentId: string): void {
    navigate(`/learn/${enrollmentId}`);
  }

  function handleOpenFeedback(courseId: string): void {
    navigate(`/courses/${courseId}/feedback`);
  }

  if (isLoading) return <div className={styles.page}><p className={styles.loading}>{t('common.loading')}</p></div>;
  if (error) return <div className={styles.page}><p className={styles.error}>{t('common.error.generic')}</p></div>;

  return (
    <div className={styles.page}>
      {/* ------------------------------------------------------------------ */}
      {/* Top-level tabs (My Learning / Course Catalogue)                      */}
      {/* ------------------------------------------------------------------ */}
      <div className={styles.tabs} role="tablist">
        <button
          role="tab"
          aria-selected={activeTab === 'my_learning'}
          className={`${styles.tab} ${activeTab === 'my_learning' ? styles.tabActive : ''}`}
          onClick={() => { setActiveTab('my_learning'); navigate('/learn'); }}
        >
          {t('learn.myLearningTab')}
          {enrollments.length > 0 && <span className={styles.tabBadge}>{enrollments.length}</span>}
        </button>
        <button
          role="tab"
          aria-selected={activeTab === 'catalog'}
          className={`${styles.tab} ${activeTab === 'catalog' ? styles.tabActive : ''}`}
          onClick={() => { setActiveTab('catalog'); navigate('/catalog'); }}
        >
          {t('learn.catalogTab', 'Course Catalogue')}
        </button>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* MY LEARNING TAB (redesigned — mirrors DashboardPage patterns)        */}
      {/* ------------------------------------------------------------------ */}
      {activeTab === 'my_learning' && (
        <div className={styles.myLearningWrapper}>

          {/* Page header */}
          <div className={styles.pageHeader}>
            <div className={styles.pageHeaderLeft}>
              <span className={styles.eyebrow}>{t('learn.eyebrow', 'My Learning')}</span>
              <h1 className={styles.pageTitle}>
                {firstName
                  ? t('learn.welcomeBack', { name: firstName, defaultValue: 'Welcome back, {{name}}' })
                  : t('learn.title')}
              </h1>
              <p className={styles.pageSubtitle}>
                {stats.inProgress} {t('learn.stats.inProgress', 'in progress')}
                <span className={styles.subtitleDot}>·</span>
                {stats.completed} {t('learn.stats.completed', 'completed')}
              </p>
            </div>
            <div className={styles.pageHeaderRight}>
              <AgentLevelBadge level={agentLevel} showLabel />
              <button
                className={styles.browseBtn}
                type="button"
                onClick={() => { setActiveTab('catalog'); navigate('/catalog'); }}
              >
                {t('learn.browseCatalog', 'Browse Catalogue')}
              </button>
            </div>
          </div>

          {/* Stats summary */}
          <div className={styles.statsRow}>
            <StatCard accent="blue"   value={stats.inProgress}   label={t('learn.stats.inProgress', 'In Progress')} />
            <StatCard accent="green"  value={stats.completed}    label={t('learn.stats.completed', 'Completed')} />
            <StatCard accent="orange" value={stats.mandatory}    label={t('learn.stats.mandatory', 'Mandatory')} />
            <StatCard accent="purple" value={stats.certificates} label={t('learn.stats.certificates', 'Certificates')} />
          </div>

          {/* Continue learning hero */}
          {continueEnrollment && (() => {
            const e = continueEnrollment;
            const pct = e.modules_total > 0 ? Math.round((e.modules_completed / e.modules_total) * 100) : 0;
            const gradient = MODULE_GRADIENTS[e.module_type] ?? MODULE_GRADIENTS.reading;
            return (
              <div className={styles.continueCard}>
                <div className={styles.continueThumb} style={{ background: gradient }}>
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.95)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 3l14 9-14 9V3z" />
                  </svg>
                </div>
                <div className={styles.continueBody}>
                  <span className={styles.continueLabel}>{t('learn.continueLearning', 'Continue Learning')}</span>
                  <h2 className={styles.continueTitle}>{e.course_title}</h2>
                  <p className={styles.continueMeta}>
                    {t('learn.moduleOf', { current: e.modules_completed + 1, total: e.modules_total, defaultValue: 'Module {{current}} of {{total}}' })}
                    <span className={styles.subtitleDot}>·</span>
                    {e.module_type.charAt(0).toUpperCase() + e.module_type.slice(1)}
                  </p>
                  <div className={styles.continueProgress}>
                    <div className={styles.continueProgressTrack}>
                      <div className={styles.continueProgressFill} style={{ width: `${pct}%` }} />
                    </div>
                    <span className={styles.continueProgressPct}>{pct}%</span>
                  </div>
                  <button className={styles.continueBtn} type="button" onClick={() => handleContinue(e.id)}>
                    {t('learn.continueButton', 'Continue')} →
                  </button>
                </div>
              </div>
            );
          })()}

          {/* Course sections (or empty state) */}
          {enrollments.length === 0 ? (
            <div className={styles.emptyCard}>
              <span className={styles.emptyIcon}>📚</span>
              <p className={styles.emptyMessage}>{t('learn.emptyMyLearning')}</p>
              <button className={styles.exploreCta} type="button" onClick={() => { setActiveTab('catalog'); navigate('/catalog'); }}>
                {t('learn.exploreCoursesButton')}
              </button>
            </div>
          ) : (
            <>
              {inProgressList.length > 0 && (
                <div className={styles.sectionCard}>
                  <div className={styles.sectionHeader}>
                    <h2 className={styles.sectionTitle}>
                      {t('learn.inProgressSection', 'In Progress')}
                      <span className={styles.countChip}>{inProgressList.length}</span>
                    </h2>
                  </div>
                  <div className={styles.grid}>
                    {inProgressList.map((e) => (
                      <EnrolledCourseCard
                        key={e.id}
                        enrollment={e}
                        onContinue={handleContinue}
                        onOpenFeedback={handleOpenFeedback}
                      />
                    ))}
                  </div>
                </div>
              )}
              {completedList.length > 0 && (
                <div className={styles.sectionCard}>
                  <div className={styles.sectionHeader}>
                    <h2 className={styles.sectionTitle}>
                      {t('learn.completedSection', 'Completed')}
                      <span className={styles.countChip}>{completedList.length}</span>
                    </h2>
                  </div>
                  <div className={styles.grid}>
                    {completedList.map((e) => (
                      <EnrolledCourseCard
                        key={e.id}
                        enrollment={e}
                        onContinue={handleContinue}
                        onOpenFeedback={handleOpenFeedback}
                      />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Supporting widgets — Competency progress + Daily drill */}
          <div className={styles.auxGrid}>
            <div className={styles.sectionCard}>
              <div className={styles.sectionHeader}>
                <h2 className={styles.sectionTitle}>{t('learn.competencyProgress', 'Competency Progress')}</h2>
              </div>
              {enrollments.length > 0
                ? <TrackProgressSection tracks={trackProgress} />
                : <p className={styles.auxEmpty}>{t('learn.noProgressYet', 'No progress yet — enrol in a course to get started.')}</p>}
            </div>
            <div className={styles.sectionCard}>
              <div className={styles.sectionHeader}>
                <h2 className={styles.sectionTitle}>{t('learn.dailyPractice', 'Daily Practice')}</h2>
              </div>
              <DrillCard />
            </div>
          </div>

          <LearningPathsSection paths={learningPaths} />

          {featureFlags.ai_recommendations && (
            <RecommendationPanel
              enrolledCourseIds={new Set(enrollments.map((e) => e.course_id))}
              onEnroll={enroll}
              onNavigate={(courseId) => {
                const enrollment = enrollments.find((e) => e.course_id === courseId);
                if (enrollment) navigate(`/learn/${enrollment.id}`);
              }}
            />
          )}
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* CATALOGUE TAB                                                         */}
      {/* ------------------------------------------------------------------ */}
      {activeTab === 'catalog' && (
        <div className={styles.catalogWrapper}>

          {/* ── Filter sidebar ── */}
          <aside className={styles.filterSidebar}>

            {/* Categories */}
            <div className={styles.filterGroup}>
              <h3 className={styles.filterGroupTitle}>Categories</h3>
              {CATEGORIES.map(({ value, label }) => {
                const count = value === '' ? allRows.length : (categoryCounts[value] ?? 0);
                return (
                  <label key={value} className={`${styles.filterCheckLabel} ${filterCategory === value ? styles.filterCheckLabelActive : ''}`}>
                    <input
                      type="radio"
                      name="category"
                      value={value}
                      checked={filterCategory === value}
                      onChange={() => setFilterCategory(value)}
                    />
                    {label}
                    <span className={styles.filterCount}>({count})</span>
                  </label>
                );
              })}
            </div>

            {/* Status filters */}
            <div className={styles.filterGroup}>
              <h3 className={styles.filterGroupTitle}>Filters</h3>
              {[
                { key: 'mandatory',   label: 'Mandatory',       state: filterMandatory,   set: setFilterMandatory   },
                { key: 'assigned',    label: 'Assigned to Me',  state: filterAssigned,    set: setFilterAssigned    },
                { key: 'inprogress',  label: 'In Progress',     state: filterInProgress,  set: setFilterInProgress  },
                { key: 'completed',   label: 'Completed',       state: filterCompleted,   set: setFilterCompleted   },
              ].map(({ key, label, state, set }) => (
                <label key={key} className={`${styles.filterCheckLabel} ${state ? styles.filterCheckLabelActive : ''}`}>
                  <input type="checkbox" checked={state} onChange={(e) => set(e.target.checked)} />
                  {label}
                </label>
              ))}
            </div>

            {/* Duration */}
            <div className={styles.filterGroup}>
              <h3 className={styles.filterGroupTitle}>Duration</h3>
              <select className={styles.filterSelect} value={filterDuration} onChange={(e) => setFilterDuration(e.target.value)}>
                {DURATION_OPTIONS.map(({ value, label }) => <option key={value} value={value}>{label}</option>)}
              </select>
            </div>

            {/* Level */}
            <div className={styles.filterGroup}>
              <h3 className={styles.filterGroupTitle}>Level</h3>
              <select className={styles.filterSelect} value={filterLevel} onChange={(e) => setFilterLevel(e.target.value)}>
                {LEVEL_OPTIONS.map(({ value, label }) => <option key={value} value={value}>{label}</option>)}
              </select>
            </div>

            <button className={styles.clearFiltersBtn} onClick={clearFilters} type="button">
              ✕ Clear Filters
            </button>
          </aside>

          {/* ── Catalogue main ── */}
          <div className={styles.catalogMain}>

            {/* Page header */}
            <div className={styles.catalogHeader}>
              <h1 className={styles.catalogPageTitle}>Course Catalogue</h1>
              <div className={styles.catalogHeaderRight}>
                <div className={styles.catalogSearchWrapper}>
                  <span className={styles.catalogSearchIcon}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                    </svg>
                  </span>
                  <input
                    type="search"
                    className={styles.catalogSearchInput}
                    placeholder="Search courses…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    aria-label="Search courses"
                  />
                </div>
                {/* View toggle */}
                <div className={styles.viewToggle}>
                  <button
                    className={`${styles.viewToggleBtn} ${viewMode === 'list' ? styles.viewToggleBtnActive : ''}`}
                    onClick={() => setViewMode('list')}
                    title="List view"
                    type="button"
                    aria-label="List view"
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/>
                      <line x1="3" y1="6" x2="3.01" y2="6" strokeWidth="2.5"/><line x1="3" y1="12" x2="3.01" y2="12" strokeWidth="2.5"/><line x1="3" y1="18" x2="3.01" y2="18" strokeWidth="2.5"/>
                    </svg>
                  </button>
                  <button
                    className={`${styles.viewToggleBtn} ${viewMode === 'grid' ? styles.viewToggleBtnActive : ''}`}
                    onClick={() => setViewMode('grid')}
                    title="Grid view"
                    type="button"
                    aria-label="Grid view"
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
                      <rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            {/* Status tabs */}
            <div className={styles.statusTabs}>
              {([
                { key: 'all',         label: 'All Courses'  },
                { key: 'mandatory',   label: 'Mandatory'    },
                { key: 'in_progress', label: 'In Progress'  },
                { key: 'completed',   label: 'Completed'    },
              ] as { key: StatusFilter; label: string }[]).map(({ key, label }) => (
                <button
                  key={key}
                  className={`${styles.statusTab} ${statusFilter === key ? styles.statusTabActive : ''}`}
                  onClick={() => setStatusFilter(key)}
                  type="button"
                >
                  {label}
                  <span className={styles.statusTabCount}>{statusCounts[key]}</span>
                </button>
              ))}
            </div>

            {/* Course area */}
            <div className={styles.courseArea}>
              <span className={styles.courseCount}>
                Showing {Math.min((page - 1) * PAGE_SIZE + 1, filteredRows.length)}–{Math.min(page * PAGE_SIZE, filteredRows.length)} of {filteredRows.length} courses
              </span>

              {filteredRows.length === 0 ? (
                <p className={styles.emptyMessage}>{t('learn.catalogEmpty')}</p>
              ) : viewMode === 'list' ? (
                <div className={styles.courseList}>
                  {pagedRows.map((row) => (
                    <CourseListRow
                      key={row.id}
                      row={row}
                      onContinue={handleContinue}
                      onStart={(id) => void handleEnroll(id)}
                      onOpenFeedback={handleOpenFeedback}
                      enrollingId={enrollingId}
                    />
                  ))}
                </div>
              ) : (
                <div className={styles.catalogGrid}>
                  {catalogForGrid.map((c) => (
                    <CatalogCourseCard
                      key={c.id}
                      course={c}
                      onEnroll={(id) => void handleEnroll(id)}
                      onContinue={handleContinue}
                      isEnrolling={enrollingId === c.id}
                      onOpenFeedback={handleOpenFeedback}
                    />
                  ))}
                </div>
              )}

              {/* Pagination (list view) */}
              {viewMode === 'list' && totalPages > 1 && (
                <div className={styles.pagination}>
                  <div className={styles.paginationBtns}>
                    <button
                      className={`${styles.pageBtn} ${page === 1 ? styles.pageBtnDisabled : ''}`}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                      aria-label="Previous page"
                      type="button"
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>
                    </button>
                    {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => i + 1).map((n) => (
                      <button
                        key={n}
                        className={`${styles.pageBtn} ${n === page ? styles.pageBtnActive : ''}`}
                        onClick={() => setPage(n)}
                        type="button"
                      >
                        {n}
                      </button>
                    ))}
                    {totalPages > 5 && <span className={styles.pageBtn} style={{ border: 'none', background: 'none' }}>…</span>}
                    {totalPages > 5 && (
                      <button className={`${styles.pageBtn} ${page === totalPages ? styles.pageBtnActive : ''}`} onClick={() => setPage(totalPages)} type="button">
                        {totalPages}
                      </button>
                    )}
                    <button
                      className={`${styles.pageBtn} ${page === totalPages ? styles.pageBtnDisabled : ''}`}
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      aria-label="Next page"
                      type="button"
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6"/></svg>
                    </button>
                  </div>
                  <span className={styles.paginationInfo}>
                    Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filteredRows.length)} of {filteredRows.length} courses
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
